import { Controller } from "@hotwired/stimulus"

// In-app feature tour (pages/feature_tour). The deck of screens is the menu:
// whichever card is centred picks the panel shown under it, the counter and
// rail say where you are, and the arrows step through with wrap-around, the
// same as the onboarding widget pager. No dots, so any number of subjects fits.
//
// The counter is an odometer: each digit is a 0-9 column slid to its value,
// so fast swipes never strand a digit mid-roll.
const DIGIT_HEIGHT = 24
const FADE_MS = 180

export default class extends Controller {
  static targets = ["deck", "card", "heading", "panel", "number", "fill"]

  connect() {
    this.current = -1
    this.width = Math.max(2, String(this.cardTargets.length).length)
    this.numberTarget.innerHTML = Array.from({ length: this.width }, () =>
      `<span class="ft-num-col"><span class="ft-num-strip">${[...Array(10).keys()].map((d) => `<i>${d}</i>`).join("")}</span></span>`
    ).join("")
    this.strips = [...this.numberTarget.querySelectorAll(".ft-num-strip")]
    this.sync()
  }

  disconnect() {
    clearTimeout(this.fadeTimer)
  }

  sync() {
    const index = this.centredIndex()
    if (index === this.current) return

    this.current = index
    this.cardTargets.forEach((card, i) => card.classList.toggle("is-on", i === index))
    this.setNumber(index + 1)
    this.fillTarget.style.width = `${((index + 1) / this.cardTargets.length) * 100}%`
    this.showHeading(index)
    this.showPanel(index)
  }

  previous() {
    this.go(this.current - 1)
  }

  next() {
    this.go(this.current + 1)
  }

  go(index) {
    const total = this.cardTargets.length
    const card = this.cardTargets[(index + total) % total]
    const deck = this.deckTarget
    deck.scrollTo({ left: card.offsetLeft + card.offsetWidth / 2 - deck.clientWidth / 2, behavior: "smooth" })
  }

  centredIndex() {
    const deck = this.deckTarget
    const middle = deck.scrollLeft + deck.clientWidth / 2
    let best = 0
    let distance = Infinity
    this.cardTargets.forEach((card, i) => {
      const d = Math.abs(card.offsetLeft + card.offsetWidth / 2 - middle)
      if (d < distance) { distance = d; best = i }
    })
    return best
  }

  setNumber(value) {
    String(value).padStart(this.width, "0").split("").forEach((digit, i) => {
      this.strips[i].style.transform = `translateY(${-digit * DIGIT_HEIGHT}px)`
    })
  }

  // Headings share one grid cell in the hero, so they cross-fade by class.
  showHeading(index) {
    this.headingTargets.forEach((heading, i) => {
      heading.classList.toggle("is-on", i === index)
      heading.toggleAttribute("aria-hidden", i !== index)
    })
  }

  // Fade the visible panel out, swap, fade the new one in. Fades in place:
  // a sideways slide read as a wiggle while the deck was moving.
  showPanel(index) {
    clearTimeout(this.fadeTimer)
    const visible = this.panelTargets.find((panel) => !panel.hidden)
    const next = this.panelTargets[index]
    if (!visible || visible === next) {
      this.panelTargets.forEach((panel, i) => { panel.hidden = i !== index })
      return
    }

    visible.classList.add("is-fading")
    this.fadeTimer = setTimeout(() => {
      this.panelTargets.forEach((panel, i) => { panel.hidden = i !== index; panel.classList.toggle("is-fading", i === index) })
      requestAnimationFrame(() => requestAnimationFrame(() => next.classList.remove("is-fading")))
    }, FADE_MS)
  }
}
